import React, { useEffect, lazy, Suspense } from 'react';
import './App.css';
import { Routes, Route, Navigate } from 'react-router-dom';
import { FormPopupModal } from './components/FormPopupModal';
import { ConsentBanner } from './components/ConsentBanner';
import { useConsent } from './contexts/ConsentContext';
import { API_BASE } from './config/api';
import { initGTMDeferred } from './utils/gtmLoader';
import { injectIntegrations } from './utils/integrationInjector';
import { initializeLogger, updateRoute } from './utils/logger';

// Public pages - loaded immediately
import Home from './pages/Home';
import Universities from './pages/Universities';
import UniversityDetail from './pages/UniversityDetail';
import Programs from './pages/Programs';
import ProgramDetail from './pages/ProgramDetail';
import ScholarshipListing from './pages/ScholarshipListing';
import ScholarshipDetail from './pages/ScholarshipDetail';
import BlogListing from './pages/BlogListing';
import BlogDetail from './pages/BlogDetail';
import StaticPage from './pages/StaticPage';
import FormPage from './pages/FormPage';

// Admin pages - static imports for instant loading and stability
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import LeadManagement from './pages/admin/LeadManagement';
import LeadDetail from './pages/admin/LeadDetail';
import AdminSettings from './pages/admin/AdminSettings';
import UniversityList from './pages/admin/UniversityList';
import UniversityForm from './pages/admin/UniversityForm';
import ProgramList from './pages/admin/ProgramList';
import ProgramForm from './pages/admin/ProgramForm';
import ScholarshipList from './pages/admin/ScholarshipList';
import ScholarshipForm from './pages/admin/ScholarshipForm';
import BlogList from './pages/admin/BlogList';
import BlogForm from './pages/admin/BlogForm';
import HomepageSettings from './pages/admin/HomepageSettings';
import TaxonomyManager from './pages/admin/TaxonomyManager';
import PageList from './pages/admin/PageList';
import PageForm from './pages/admin/PageForm';
import HeaderSettings from './pages/admin/HeaderSettings';
import FooterSettings from './pages/admin/FooterSettings';
import BrandSettings from './pages/admin/BrandSettings';
import AdminProfile from './pages/admin/AdminProfile';
import ResetPassword from './pages/admin/ResetPassword';
import FieldRegistry from './pages/admin/FieldRegistry';
import FormBuilder from './pages/admin/FormBuilder';
import AuditLogs from './pages/admin/AuditLogs';
import SystemLogs from './pages/admin/SystemLogs';
import AIInstructionHints from './pages/admin/AIInstructionHints';
import AIDataGenerator from './pages/admin/AIDataGenerator';
import { AnalyticsOverview } from './pages/admin/AnalyticsOverview';

// CRM feature flags
import { isCrmEnabled } from './utils/featureFlags';
import { CrmDisabled } from './pages/crm/CrmDisabled';
import { CrmAuthGuard } from './components/crm/CrmAuthGuard';
import { AdminAuthGuard } from './components/crm/AdminAuthGuard';
import { CounselorAuthGuard } from './components/crm/CounselorAuthGuard';
import CrmLogin from './pages/crm/CrmLogin';
import CrmAdminLogin from './pages/crm-admin/AdminLogin';
import CounselorLogin from './pages/crm/CounselorLogin';

// CRM pages - lazy loaded to avoid impacting public site bundle
const CrmStudentDashboard = lazy(() => import('./pages/crm/StudentDashboard').catch(() => ({ default: () => <CrmDisabled /> })));
const CrmParentDashboard = lazy(() => import('./pages/crm/ParentDashboard').catch(() => ({ default: () => <CrmDisabled /> })));
const CrmCounselorDashboard = lazy(() => import('./pages/crm/CounselorDashboard').catch(() => ({ default: () => <CrmDisabled /> })));
const CrmAdminDashboard = lazy(() => import('./pages/crm-admin/CrmAdminDashboard').catch(() => ({ default: () => <CrmDisabled /> })));
const CrmOnboarding = lazy(() => import('./pages/crm/CrmOnboarding').catch(() => ({ default: () => <CrmDisabled /> })));

function App() {
  const { consent, isLoaded } = useConsent();
  
  // Initialize logger on app mount
  useEffect(() => {
    initializeLogger();
    updateRoute(window.location.pathname);
    
    // Update route on navigation
    const handleLocationChange = () => {
      updateRoute(window.location.pathname);
    };
    window.addEventListener('popstate', handleLocationChange);
    return () => window.removeEventListener('popstate', handleLocationChange);
  }, []);

  // Fetch and set favicon dynamically from brand settings
  useEffect(() => {
    const fetchBrandSettings = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/public/brand-settings`);
        if (response.ok) {
          const data = await response.json();
          const { favicon } = data.settings;
          
          // Update favicon 48x48
          if (favicon?.favicon_48) {
            const favicon48 = document.getElementById('favicon-48');
            if (favicon48) {
              favicon48.href = favicon.favicon_48;
            }
          }
          
          // Update favicon 192x192
          if (favicon?.favicon_192) {
            const favicon192 = document.getElementById('favicon-192');
            if (favicon192) {
              favicon192.href = favicon.favicon_192;
            }
          }
        }
      } catch (err) {
        console.log('Using default favicon');
      }
    };
    fetchBrandSettings();
  }, []);

  // Load GTM dynamically (deferred, non-blocking)
  useEffect(() => {
    const loadGTM = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/public/analytics/gtm-config`);
        if (response.ok) {
          const data = await response.json();
          if (data.gtm?.enabled && data.gtm?.container_id) {
            initGTMDeferred(data.gtm.container_id);
          }
        }
      } catch (err) {
        // Fail silently - no console error
      }
    };
    loadGTM();
  }, []);

  // Load integrations dynamically (deferred, non-blocking)
  // Re-run when consent changes
  useEffect(() => {
    if (!isLoaded) return; // Wait for consent to load
    
    const loadIntegrations = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/public/integrations`);
        if (response.ok) {
          const data = await response.json();
          if (data.integrations && Array.isArray(data.integrations) && data.integrations.length > 0) {
            const currentEnv = process.env.NODE_ENV === 'production' ? 'production' : 'development';
            // Pass consent state to injector
            injectIntegrations(data.integrations, currentEnv, consent);
          }
        }
      } catch (err) {
        // Fail silently - no console error
      }
    };
    loadIntegrations();
  }, [consent, isLoaded]);

  return (
    <div className="App">
      <ConsentBanner />
      <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/universities" element={<Universities />} />
          <Route path="/universities/:slug" element={<UniversityDetail />} />
          <Route path="/programs" element={<Programs />} />
          <Route path="/programs/:slug" element={<ProgramDetail />} />
          <Route path="/scholarships" element={<ScholarshipListing />} />
          <Route path="/scholarships/:slug" element={<ScholarshipDetail />} />
          <Route path="/blogs" element={<BlogListing />} />
          <Route path="/blogs/:slug" element={<BlogDetail />} />
          
          {/* Public Form Pages */}
          <Route path="/forms/test-contact" element={<div style={{padding: '50px', fontSize: '24px', textAlign: 'center'}}>FORM ROUTE WORKS</div>} />
          <Route path="/forms/:slug" element={<FormPage />} />
          
          {/* Admin Routes - Static imports for instant loading */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin/leads" element={<LeadManagement />} />
          <Route path="/admin/leads/:id" element={<LeadDetail />} />
          <Route path="/admin/settings" element={<AdminSettings />} />
          <Route path="/admin/analytics" element={<AnalyticsOverview />} />
          
          {/* Universities */}
          <Route path="/admin/universities" element={<UniversityList />} />
          <Route path="/admin/universities/new" element={<UniversityForm />} />
          <Route path="/admin/universities/:id/edit" element={<UniversityForm />} />
          
          {/* Programs */}
          <Route path="/admin/programs" element={<ProgramList />} />
          <Route path="/admin/programs/new" element={<ProgramForm />} />
          <Route path="/admin/programs/:id/edit" element={<ProgramForm />} />
          
          {/* Scholarships */}
          <Route path="/admin/scholarships" element={<ScholarshipList />} />
          <Route path="/admin/scholarships/new" element={<ScholarshipForm />} />
          <Route path="/admin/scholarships/:id/edit" element={<ScholarshipForm />} />
          
          {/* Blog Posts */}
          <Route path="/admin/blogs" element={<BlogList />} />
          <Route path="/admin/blogs/new" element={<BlogForm />} />
          <Route path="/admin/blogs/:id/edit" element={<BlogForm />} />
          
          {/* Homepage & Taxonomy */}
          <Route path="/admin/homepage" element={<HomepageSettings />} />
          <Route path="/admin/taxonomy" element={<TaxonomyManager />} />
          
          {/* Form Builder */}
          <Route path="/admin/form-fields" element={<FieldRegistry />} />
          <Route path="/admin/forms-builder" element={<FormBuilder />} />
          
          {/* Static Pages */}
          <Route path="/admin/pages" element={<PageList />} />
          <Route path="/admin/pages/new" element={<PageForm />} />
          <Route path="/admin/pages/:id/edit" element={<PageForm />} />
          
          {/* Header & Footer & Brand Settings */}
          <Route path="/admin/header" element={<HeaderSettings />} />
          <Route path="/admin/footer" element={<FooterSettings />} />
          <Route path="/admin/brand" element={<BrandSettings />} />
          
          {/* Admin Profile & Password */}
          <Route path="/admin/profile" element={<AdminProfile />} />
          <Route path="/admin/reset-password" element={<ResetPassword />} />
          
          {/* Audit Logs */}
          <Route path="/admin/audit-logs" element={<AuditLogs />} />
          
          {/* System Logs */}
          <Route path="/admin/system-logs" element={<SystemLogs />} />
          <Route path="/admin/system-logs/:logId" element={<SystemLogs />} />
          
          {/* AI Configuration */}
          <Route path="/admin/ai-instruction-hints" element={<AIInstructionHints />} />
          <Route path="/admin/ai-data-generator" element={<AIDataGenerator />} />
          
          {/* Public Static Pages */}
          <Route path="/pages/:slug" element={<StaticPage />} />
          
          {/* CRM Routes - Gated by feature flag */}
          {isCrmEnabled() ? (
            <>
              {/* CRM Login Pages */}
              <Route path="/crm/admin/login" element={<CrmAdminLogin />} />
              <Route path="/crm/counselor/login" element={<CounselorLogin />} />
              
              {/* Legacy routes - redirect to new paths */}
              <Route path="/crm-admin/login" element={<Navigate to="/crm/admin/login" replace />} />
              <Route path="/crm/login" element={<Navigate to="/crm/counselor/login" replace />} />
              <Route path="/crm/login-legacy" element={<CrmLogin />} />
              
              {/* CRM Onboarding */}
              <Route 
                path="/crm/onboarding" 
                element={
                  <CrmAuthGuard>
                    <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
                      <CrmOnboarding />
                    </Suspense>
                  </CrmAuthGuard>
                } 
              />
              
              {/* Student Portal */}
              <Route 
                path="/crm/student/*" 
                element={
                  <CrmAuthGuard>
                    <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
                      <CrmStudentDashboard />
                    </Suspense>
                  </CrmAuthGuard>
                } 
              />
              {/* Parent Portal */}
              <Route 
                path="/crm/parent/*" 
                element={
                  <CrmAuthGuard>
                    <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
                      <CrmParentDashboard />
                    </Suspense>
                  </CrmAuthGuard>
                } 
              />
              {/* Counselor Portal */}
              <Route 
                path="/crm/counselor/*" 
                element={
                  <CounselorAuthGuard>
                    <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
                      <CrmCounselorDashboard />
                    </Suspense>
                  </CounselorAuthGuard>
                } 
              />
              {/* CRM Admin Console */}
              <Route 
                path="/crm/admin/*" 
                element={
                  <AdminAuthGuard>
                    <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
                      <CrmAdminDashboard />
                    </Suspense>
                  </AdminAuthGuard>
                } 
              />
              {/* Legacy admin route - redirect */}
              <Route path="/crm-admin/*" element={<Navigate to="/crm/admin" replace />} />
            </>
          ) : (
            <>
              {/* Show disabled page for all CRM routes when disabled */}
              <Route path="/crm/admin/login" element={<CrmDisabled />} />
              <Route path="/crm/counselor/login" element={<CrmDisabled />} />
              <Route path="/crm/student/*" element={<CrmDisabled />} />
              <Route path="/crm/parent/*" element={<CrmDisabled />} />
              <Route path="/crm/counselor/*" element={<CrmDisabled />} />
              <Route path="/crm/admin/*" element={<CrmDisabled />} />
              {/* Legacy routes */}
              <Route path="/crm-admin/*" element={<CrmDisabled />} />
              <Route path="/crm/login" element={<CrmDisabled />} />
            </>
          )}
        </Routes>
        
        {/* Global Form Popup Modal */}
        <FormPopupModal />
    </div>
  );
}

export default App;
